/**
 * AdjustTimesButton — admin-only fix-up modal for clock/en-route/lunch on a WO.
 *
 * PATCH /api/jobs/{id}/time with any of:
 *   clock_in_at, clock_out_at, en_route_at, paused_at, lunch_start_at,
 *   total_lunch_minutes, plus clear_* booleans, plus optional reason
 *
 * Common usage: worker forgot to clock out at 5pm — office sets clock_out_at
 * manually + logs the reason. Every save is stamped into the WO timeline.
 */
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, ClockCounterClockwise } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Convert an ISO string to the "YYYY-MM-DDTHH:MM" value <input type=datetime-local>
// expects. Empty on invalid so the field renders blank instead of "NaN-NaN-NaN".
const toLocalInput = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
};

// Reverse of toLocalInput — take a "YYYY-MM-DDTHH:MM" wall-clock string and
// return a proper UTC ISO. Without this the backend would treat the local
// string as UTC and shift the time by the browser's offset (3:07pm EDT →
// stored as 15:07 UTC → read back as 11:07am EDT — the "old" time bug).
const localInputToUtcIso = (localStr) => {
  if (!localStr) return null;
  const d = new Date(localStr); // browser reads as local wall-clock
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
};

export function AdjustTimesButton({ job, onSaved, size = "md", label, testid = "adjust-times-btn" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (user?.role !== "admin") return null; // hard admin-only gate — mirror the backend
  const cls = size === "sm"
    ? "inline-flex items-center gap-1 h-8 px-2.5 border-2 border-[#0f172a] bg-white hover:bg-slate-100 text-slate-900 text-[10px] font-black uppercase tracking-widest"
    : "inline-flex items-center gap-1.5 h-9 px-3 border-2 border-[#0f172a] bg-white hover:bg-slate-100 text-slate-900 text-[11px] font-black uppercase tracking-widest";
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={testid}
        title="Admin only — fix or backfill clock, en-route, pause, and lunch times"
        className={cls}>
        <ClockCounterClockwise weight="bold" size={size === "sm" ? 12 : 14}/>
        {label || "Adjust times"}
      </button>
      {open && (
        <AdjustTimesModal
          job={job}
          onClose={() => setOpen(false)}
          onSaved={(j) => { setOpen(false); onSaved && onSaved(j); }}
        />
      )}
    </>
  );
}

function AdjustTimesModal({ job, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [f, setF] = useState({
    clock_in_at:    toLocalInput(job?.clock_in_at),
    clock_out_at:   toLocalInput(job?.clock_out_at),
    en_route_at:    toLocalInput(job?.en_route_at),
    paused_at:      toLocalInput(job?.paused_at),
    lunch_start_at: toLocalInput(job?.lunch_start_at),
    total_lunch_minutes: job?.total_lunch_minutes ?? "",
  });
  const upd = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  // Auto-fix suggestions — the office's #1 use case is "worker forgot to
  // clock out but the WO is marked complete". We detect the gap and offer
  // a one-tap fix pre-filling the clock-out field with completed_at, plus
  // a similar pattern for missing clock-in when en_route_at exists.
  const suggestions = useMemo(() => {
    const out = [];
    const hasCompleted = job?.completed_at;
    const missingClockOut = !job?.clock_out_at;
    const hasClockIn = job?.clock_in_at;
    if (hasCompleted && missingClockOut && hasClockIn) {
      out.push({
        id: "clock_out_from_completed",
        title: "Clock OUT missing but WO was completed",
        subtitle: `Suggest ${new Date(job.completed_at).toLocaleString()} (WO completion time)`,
        apply: () => setF((s) => ({ ...s, clock_out_at: toLocalInput(job.completed_at) })),
        cta: "Apply completion time",
      });
    }
    const hasEnRoute = job?.en_route_at;
    const missingClockIn = !job?.clock_in_at;
    if (hasEnRoute && missingClockIn) {
      out.push({
        id: "clock_in_from_en_route",
        title: "Clock IN missing but tech marked On-my-way",
        subtitle: `Suggest ${new Date(job.en_route_at).toLocaleString()} (on-my-way stamp)`,
        apply: () => setF((s) => ({ ...s, clock_in_at: toLocalInput(job.en_route_at) })),
        cta: "Apply on-my-way time",
      });
    }
    return out;
  }, [job]);

  const original = {
    clock_in_at:    toLocalInput(job?.clock_in_at),
    clock_out_at:   toLocalInput(job?.clock_out_at),
    en_route_at:    toLocalInput(job?.en_route_at),
    paused_at:      toLocalInput(job?.paused_at),
    lunch_start_at: toLocalInput(job?.lunch_start_at),
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Only send the fields that changed. Empty string on a previously
      // non-empty field means "clear it" — routed through clear_* flag.
      const body = {};
      const wire = (localKey, apiKey, clearKey) => {
        const now = (f[localKey] || "").trim();
        const was = original[localKey];
        if (now === was) return;
        if (now === "" && was !== "") body[clearKey] = true;
        else if (now !== "") body[apiKey] = localInputToUtcIso(now);
      };
      wire("clock_in_at",    "clock_in_at",    "clear_clock_in");
      wire("clock_out_at",   "clock_out_at",   "clear_clock_out");
      wire("en_route_at",    "en_route_at",    "clear_en_route");
      wire("paused_at",      "paused_at",      "clear_paused");
      wire("lunch_start_at", "lunch_start_at", "clear_lunch_start");
      const tl = String(f.total_lunch_minutes ?? "").trim();
      const wasTl = String(job?.total_lunch_minutes ?? "").trim();
      if (tl !== wasTl && tl !== "") {
        const n = parseInt(tl, 10);
        if (Number.isFinite(n) && n >= 0) body.total_lunch_minutes = n;
      }
      if (Object.keys(body).length === 0) {
        toast.error("Nothing changed — edit a field first");
        setBusy(false);
        return;
      }
      const r = (reason || "").trim();
      if (r) body.reason = r.slice(0, 280);
      const { data } = await api.patch(`/jobs/${job.id}/time`, body);
      toast.success("Times updated");
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't save");
    } finally { setBusy(false); }
  };

  const Row = ({ label, k, sub }) => (
    <label className="block">
      <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</span>
      {sub && <span className="ml-2 text-[10px] text-slate-400">{sub}</span>}
      <div className="mt-1 flex items-center gap-2">
        <input type="datetime-local" value={f[k]} onChange={upd(k)}
          data-testid={`adjust-times-${k}`}
          className="flex-1 h-11 px-2 border-2 border-[#0f172a] text-sm bg-white"/>
        {f[k] && (
          <button type="button" onClick={() => setF((s) => ({ ...s, [k]: "" }))}
            data-testid={`adjust-times-clear-${k}`}
            title="Clear this field"
            className="h-11 px-2 border-2 border-red-600 text-red-700 font-black text-[10px] uppercase tracking-widest hover:bg-red-50">
            Clear
          </button>
        )}
      </div>
    </label>
  );

  return createPortal(
    <div className="fixed inset-0 bg-black/60 flex items-start sm:items-center justify-center z-[110] p-4 overflow-y-auto"
         onClick={onClose} data-testid="adjust-times-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="w-full max-w-lg my-4 bg-white border-2 border-[#0f172a] shadow-[8px_8px_0px_#0f172a] p-6">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-display font-black text-2xl inline-flex items-center gap-2">
              <ClockCounterClockwise weight="fill" size={22}/> Adjust times
            </h3>
            <p className="mt-1 text-xs text-slate-600 font-medium">
              Admin fix-up for {job.work_order_number ? <b>WO {job.work_order_number}</b> : "this WO"}.
              Every change is stamped into the WO timeline for audit.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1" data-testid="adjust-times-close">
            <X weight="bold" size={22}/>
          </button>
        </div>

        <div className="mt-4 space-y-3">
          {suggestions.length > 0 && (
            <div className="space-y-2" data-testid="adjust-times-suggestions">
              {suggestions.map((s) => (
                <div key={s.id} data-testid={`adjust-times-suggest-${s.id}`}
                  className="p-3 border-2 border-emerald-500 bg-emerald-50 flex items-start gap-3">
                  <div className="text-2xl leading-none">💡</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-black uppercase tracking-widest text-emerald-800">
                      {s.title}
                    </div>
                    <div className="text-xs text-slate-700 mt-0.5">{s.subtitle}</div>
                  </div>
                  <button type="button" onClick={s.apply}
                    data-testid={`adjust-times-suggest-apply-${s.id}`}
                    className="shrink-0 h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest">
                    {s.cta}
                  </button>
                </div>
              ))}
            </div>
          )}
          <Row label="Clock IN"        k="clock_in_at"/>
          <Row label="Clock OUT"       k="clock_out_at"/>
          <Row label="On-my-way"       k="en_route_at"    sub="(en-route stamp)"/>
          <Row label="Paused at"       k="paused_at"      sub="(worker switched to another WO)"/>
          <Row label="Lunch started"   k="lunch_start_at" sub="(active-lunch marker)"/>
          <label className="block">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Total lunch (minutes)</span>
            <input type="number" min="0" max="1440" value={f.total_lunch_minutes}
              onChange={upd("total_lunch_minutes")}
              data-testid="adjust-times-total-lunch"
              placeholder="e.g. 30"
              className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] text-sm bg-white"/>
          </label>
          <label className="block">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              Reason (optional — appears on WO timeline)
            </span>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Worker forgot to clock out at end of shift"
              maxLength={280}
              data-testid="adjust-times-reason"
              className="mt-1 w-full h-11 px-3 border-2 border-slate-300 text-sm bg-white"/>
          </label>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose}
            className="h-11 px-4 border-2 border-slate-300 font-black uppercase text-xs tracking-widest hover:bg-slate-100"
            data-testid="adjust-times-cancel">
            Cancel
          </button>
          <button type="submit" disabled={busy}
            className="h-11 px-5 bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest disabled:opacity-40"
            data-testid="adjust-times-save">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}

export default AdjustTimesButton;
